// src/components/Student/DashboardHome.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Avatar,
  Skeleton,
  Divider,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  Grade as GradeIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  EmojiEvents as ExamResultsIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import { getDefaultAcademicYear } from '../../utils/academicYear';

const DashboardHome = ({ onNavigate, menuConfig = {} }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [quickStats, setQuickStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securityDismissed, setSecurityDismissed] = useState(false);

  const isAr = i18n.language === 'ar';
  const needsPassword = user && !user.has_custom_password;
  const needs2FA      = user && !user.totp_enabled;
  const showSecurityBanner = !securityDismissed && (needsPassword || needs2FA);

  useEffect(() => {
    loadQuickStats();
  }, []);

  const loadQuickStats = async () => {
    try {
      setIsLoading(true);
      const [gradesResponse, statsResponse] = await Promise.all([
        studentAPI.getGrades(),
        studentAPI.getGradeStats()
      ]);

      const totalGrades = Object.values(gradesResponse.grades || {})
        .flatMap(year => Object.values(year))
        .flatMap(session => Object.values(session))
        .flatMap(sessionType => Object.values(sessionType))
        .flatMap(academicYear => Object.values(academicYear))
        .flatMap(semester => semester).length;

      const recentStats = statsResponse.statistics?.[0] || {};
      const hasArabicNames = gradesResponse.has_arabic_names;

      setQuickStats({
        totalSubjects: totalGrades,
        currentYear: recentStats.academic_year || getDefaultAcademicYear(),
        passedSubjects: recentStats.passed_subjects || 0,
        hasArabicNames,
        totalYears: Object.keys(gradesResponse.grades || {}).length
      });
    } catch (err) {
      setError(t('error')); 
      console.error('Error loading quick stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-MA' : 'fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getCurrentTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return t('goodMorning');
    } else if (hour < 18) {
      return t('goodAfternoon');
    } else {
      return t('goodEvening');
    }
  };

  const QuickActionCard = ({ title, description, icon, color, onClick, disabled = false }) => (
    <Card 
      sx={{ 
        height: '100%',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': disabled ? {} : {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        },
        opacity: disabled ? 0.6 : 1
      }}
      onClick={disabled ? undefined : onClick}
    >
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            color: 'white'
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Welcome Section */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h3" fontWeight="300" gutterBottom>
                {getCurrentTimeGreeting()}! 👋
              </Typography>
              <Typography variant="h5" fontWeight="600" gutterBottom>
                {user?.nom_complet || user?.cod_etu}
              </Typography>
            
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 2 }}>
                {t('welcomeMessageBody')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  fontSize: '3rem',
                  fontWeight: 'bold',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}
                src={studentAPI.getStudentPhotoUrl(user?.cod_etu)}
              >
                {user?.nom_complet?.charAt(0) || user?.cod_etu?.charAt(0) || 'S'}
              </Avatar>
            </Grid>
          </Grid>
        </Box>
        
        {/* Decorative Elements */}
         <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 1
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            zIndex: 1
          }}
        />
      </Paper>

      {/* Security notification banner */}
      {showSecurityBanner && (
        <Paper
          elevation={2}
          sx={{
            mb: 3, borderRadius: 3, overflow: 'hidden',
            border: '1px solid #f39c12',
            background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)'
          }}
        >
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <SecurityIcon sx={{ color: '#f39c12', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#b7770d', mb: 0.5 }}>
                {isAr ? 'تنبيه أمني' : 'Avertissement de sécurité'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#7d5200', mb: 1.5 }}>
                {isAr
                  ? 'يُنصح بشدة بتأمين حسابك لحماية بياناتك الشخصية والأكاديمية.'
                  : 'Il est fortement recommandé de sécuriser votre compte pour protéger vos données personnelles et académiques.'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {needsPassword && (
                  <Box
                    onClick={() => onNavigate('profile')}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
                      bgcolor: '#f39c12', color: 'white',
                      fontWeight: 600, fontSize: '0.85rem',
                      '&:hover': { bgcolor: '#d68910' }, transition: 'background 0.2s'
                    }}
                  >
                    <LockIcon sx={{ fontSize: 16 }} />
                    {isAr ? 'تغيير كلمة المرور' : 'Changer le mot de passe'}
                  </Box>
                )}
                {needs2FA && (
                  <Box
                    onClick={() => onNavigate('profile')}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
                      bgcolor: 'white', color: '#b7770d',
                      fontWeight: 600, fontSize: '0.85rem',
                      border: '1.5px solid #f39c12',
                      '&:hover': { bgcolor: '#fff3cd' }, transition: 'background 0.2s'
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 16 }} />
                    {isAr ? 'تفعيل المصادقة الثنائية' : 'Activer la 2FA'}
                  </Box>
                )}
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setSecurityDismissed(true)} sx={{ color: '#b7770d', flexShrink: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {menuConfig['convocation'] !== false && (
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionCard
                title={isAr ? 'استدعاء الامتحان' : "Convocation d'examen"}
                description={isAr ? 'تحميل استدعاء الامتحان' : 'Télécharger votre convocation'}
                icon={<EventNoteIcon sx={{ fontSize: 30 }} />}
                color="#7c3aed"
                onClick={() => onNavigate('convocation')}
              />
            </Grid>
          )}
          {menuConfig['exam-results'] !== false && (
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionCard
                title={isAr ? 'نتائج الدورة' : 'Résultats de session'}
                description={isAr ? 'عرض نتائج الدورة الحالية وتقديم شكاية' : 'Consulter vos résultats et soumettre une réclamation'}
                icon={<ExamResultsIcon sx={{ fontSize: 30 }} />}
                color="#1565c0"
                onClick={() => onNavigate('exam-results')}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <QuickActionCard
              title={t('myProfile')}
              description={t('viewPersonalInfo')}
              icon={<PersonIcon sx={{ fontSize: 30 }} />}
              color="#3498db"
              onClick={() => onNavigate('profile')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <QuickActionCard
              title={t('pedagogicalSituation')}
              description={t('viewModules')}
              icon={<AssignmentIcon sx={{ fontSize: 30 }} />}
              color="#f39c12"
              onClick={() => onNavigate('pedagogical')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <QuickActionCard
              title={t('administrativeSituation')}
              description={t('viewAdminReg')}
              icon={<AssignmentIcon sx={{ fontSize: 30 }} />}
              color="#2340e2"
              onClick={() => onNavigate('administrative')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <QuickActionCard
              title={t('timeSchedule')}
              description={t('viewSchedule')}
              icon={<TimeIcon sx={{ fontSize: 30 }} />}
              color="#12f3ce"
              onClick={loadQuickStats}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Student Information Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom color="primary">
                📋 {t('infoSummary')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                     {t('N Apogee')}
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {user?.cod_etu || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('specialization')}
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {user?.etape || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('cinLabel')}
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {user?.cin || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('lastUpdate')}
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatDate(user?.derniere_mise_a_jour)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default DashboardHome;